<template>
  <Form
    v-if="formSchema"
    :validation-schema="formSchema"
    :initial-values="initialValues"
    @submit="submit"
    v-slot="{ meta }"
    ref="nativeForm"
    class="p-the-j-input-screen"
  >
    <div class="p-point-summary">
      <div class="p-form-group">
        <span class="p-the-j-input-screen__label">
          贈与可能ポイント
        </span>
        <PointNotation
          :font-size="24"
          :point="thanksExclusivePoint"
          :sign-is-exists="false"
        />
      </div>

      <div v-if="showUsedPoints">
        <span class="p-the-j-input-screen__label">
          合計送付ポイント
        </span>

        <PointNotation
          :font-size="24"
          :point="usedPoints"
          :sign-is-exists="false"
        />
      </div>

        <ValidationErrorText
          v-if="pointOver"
        >
          合計送付ポイントが贈与可能ポイントを超えています。
        </ValidationErrorText>
    </div>

    <div class="p-form-group">
      <FormLabel for="send-point" :required="true">送付ポイント</FormLabel>

      <div class="p-the-j-input-screen__point">
        <Field name="sendPoint" v-slot="{ value, errorMessage, handleChange }">
          <div
            v-if="pointTypeOf === 'free' || pointTypeOf === 'availablePointRange'"
            class="p-the-j-input-screen__point__col"
          >
            <div class="p-the-j-input-screen__point__input">
              <InputText
                id="send-point"
                type="text"
                :value="value"
                @input="event => {
                  handleChange(event);
                  notifySendPoint(event);
                }"
                :error="errorMessage"
                placeholder="100"
                :maxlength="20"
              />
            </div>
            <span class="p-the-j-input-screen__point__unit" data-translate-skip>
              {{ unit.point }}
            </span>
          </div>
          <div
            v-if="pointTypeOf === 'availablePointRange'"
            class="p-the-j-input-screen__point__note"
          >
            <SmallText>
              送付可能ポイント数:
              <span data-translate-skip>
                {{ addComma(availablePointRange.min) }} 〜
                {{ addComma(availablePointRange.max) }}
                {{ unit.point }}
              </span>
            </SmallText>
          </div>
          <div
            v-else-if="pointTypeOf === 'object'"
            class="p-the-j-input-screen__point__col"
          >
            <div class="p-the-j-input-screen__point__select">
              <InputSelect
                v-if="selectPoint"
                id="send-point"
                :value="value"
                @input="event => {handleChange(event); notifySendPoint(event);}"
                :error="errorMessage"
                :options="selectPoint.options"
                font-family="roboto"
              />
            </div>
            <span class="p-the-j-input-screen__point__unit" data-translate-skip>
              {{ unit.point }}
            </span>
          </div>
          <div
            v-else-if="pointTypeOf === 'number'"
            class="p-the-j-input-screen__point__col"
          >
            <div class="p-the-j-input-screen__point__text">
              <span>{{ addComma(displayFixedPoint) }}</span>
                <InputText
                  id="send-point"
                  type="hidden"
                  :value="value"
                  @input="handleChange"
                  :error="errorMessage"
                  placeholder="100"
                />
            </div>	
              <span class="p-the-j-input-screen__point__unit" data-translate-skip>
                {{ unit.point }}
              </span>
          </div>

          <ValidationErrorText v-show="errorMessage">
            {{ errorMessage }}
          </ValidationErrorText>
        </Field>
      </div>
    </div>

    <div class="p-form-group">
      <FormLabel for="title" :required="true">タイトル</FormLabel>
      <Field name="title" v-slot="{ value, errorMessage, handleChange }">
        <InputText
          v-if="titleEditEnabled"
          id="title"
          :type="titleEditEnabled ? 'text' : 'hidden'"
          :value="value"
          @input="event => {
            handleChange(event);
            onTitleChange(event);
          }"
          :error="errorMessage"
          placeholder
        />
        <span v-else>{{ value }}</span>
        <p v-if="titleEditEnabled" class="p-cmt">全半角100文字以内</p>
        <ValidationErrorText v-show="errorMessage">
          {{ errorMessage }}
        </ValidationErrorText>
      </Field>
    </div>

    <div class="p-form-group">
      <FormLabel for="message" :required="true">本文</FormLabel>
      <Field name="message" v-slot="{ value, errorMessage, handleChange }">
        <InputTextarea
          id="message"
          :value="value"
          @input="event => {
            handleChange(event);
            onMessageChange(event);
          }"
          :error="errorMessage"
          :add-style="{ height: `${280 / 16}rem` }"
          :placeholder="defaultMessage"
        />
        <p class="p-cmt">全半角500文字以内</p>
        <ValidationErrorText v-show="errorMessage">
          {{ errorMessage }}
        </ValidationErrorText>
      </Field>
    </div>

    <input type="hidden" name="giving_type" :value="givingType" />

    <div class="p-form-button-group">
      <div class="p-form-button-group__vertical">
        <ButtonMain
          type="submit"
          :disabled="!(meta.valid && canSubmit)"
          :style="!(meta.valid && canSubmit) ? { background: '#e0e0e0' } : { background: colorCode }"
        >
          入力内容を確認する
        </ButtonMain>
      </div>
    </div>
  </Form>
</template>

<script lang="ts">
import { mapState } from "vuex";
import { Options, Vue } from "vue-class-component";
import { Field, Form } from "vee-validate";
import * as yup from "yup";

import InputText from "@/common/components/atoms/InputText.vue";
import InputTextarea from "@/common/components/atoms/InputTextarea.vue";
import InputSelect from "@/common/components/atoms/InputSelect.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import PointNotation from "@/common/components/atoms/PointNotation.vue";
import SmallText from "@/common/components/atoms/SmallText.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";
import FormLabel from "@/common/components/organisms/FormLabel.vue";

import commons from "@/common/store/modules/commons";

@Options({
  name: "TheJInputScreen",
  emits: ["send-point-change", "show-confirm"],
  components: {
    InputText,
    InputTextarea,
    InputSelect,
    ButtonMain,
    PointNotation,
    SmallText,
    FormLabel,
    ValidationErrorText,
    Field,
    Form
  },
  computed: {
    ...mapState({
      colorCode: () => commons.colorCode
    })
  },
  props: {
    availablePoints: Number,
    pointsToSend: [String, Array, Number],
    maxPoints: {
      type: [Number, Boolean],
      default: false
    },
    minPoints: {
      type: [Number, Boolean],
      default: false
    },
    givingType: {
      type: Number,
      default: 1
    },
    defaultMessage: String,
    fixedPoint: Number,
    usedPoints: {
      type: Number,
      default: 0
    },
    selectedUserCount: {
      type: Number,
      default: 0
    },
    badgeTitle: {
      type: String,
      default: ""
    },
    badgeMessage: {
      type: String,
      default: ""
    },
    giftBadgeRequired: {
      type: Boolean,
      default: false
    },
    selectedGiftBadgeId: {
      type: Number,
      default: null
    },
    titleEditEnabled: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    givingType() {
      this.initFormSchema();

      if (this.pointTypeOf === "number") {
        this.$nextTick(() => {
          const form = this.$refs.nativeForm as any;

          form?.setFieldValue(
            "sendPoint",
            this.fixedPoint
          );

          this.notifySendPoint(this.fixedPoint);
        });
      }
    },
    maxPoints() {
      this.initFormSchema();
    },
    minPoints() {
      this.initFormSchema();
    },
    fixedPoint: {
      immediate: true,
      handler(value) {
        if (this.pointTypeOf === "number") {
          this.notifySendPoint(value);
        }
      }
    },
    titleEditEnabled() {
      this.initFormSchema();
    },
    pointsToSend() {
      this.initFormSchema();
    },
  }
})
export default class TheJInputScreen extends Vue {
  availablePoints!: number;
  pointsToSend!: string | number | any;
  maxPoints!: number;
  minPoints!: number ;
  givingType!: number;
  defaultMessage!: string;
  fixedPoint!: number;

  formSchema: any = null;

  usedPoints!: number;
  selectedUserCount!: number;

  badgeTitle!: string;
  badgeMessage!: string;

  giftBadgeRequired!: boolean;
  selectedGiftBadgeId!: number | null;
  titleEditEnabled!: boolean;
  titleEdited = false;
  messageEdited = false;

  get selectPoint(): any {
    if (this.pointTypeOf !== "object" || !this.pointsToSend) {
      return false;
    }

    const value = this.pointsToSend.toString().replace(/^\[|\]$/g, "");

    const optionsArray = value.split(",").map((res: any) => ({
      value: res.trim(),
      label: res.trim()
    }));

    return {current: "", options: optionsArray};
  }

  get unit() {
    return commons.unit;
  }

  get pointTypeOf() {
    switch (this.givingType) {
      case 0: return "free";
      case 1: return "object";
      case 2: return "number";
      case 3: return "availablePointRange";
      default: return "free";
    }
  }

  get availablePointRange() {
    const max = typeof this.maxPoints === 'number' ? this.maxPoints : undefined;
    const min = typeof this.minPoints === 'number' ? this.minPoints : undefined;
    if (typeof max !== 'number' || typeof min !== 'number') return false;
    return { max, min };
  }

  get thanksExclusivePoint() {
    return this.availablePoints;
  }
  
  get initialValues() {
    return {
      sendPoint:
        this.pointTypeOf === "number"
          ? this.fixedPoint
          : "",
      title: this.badgeTitle,
      message: this.badgeMessage
    };
  }

  created() {
    this.initFormSchema();
  }

  addComma(value: any) {
    const number = Number(value);
    if (Number.isNaN(number)) {
      return value;
    }
    return number.toLocaleString();
  }

  initFormSchema() {
    const fallbackMax = typeof this.availablePoints === 'number' ? this.availablePoints : 999999;

    const min = 0;

    const pointRuleMap: Record<string, yup.AnySchema> = {
      free: yup.number()
        .typeError("送付するポイントは数字で入力してください。")
        .integer("送付するポイントは整数で入力してください。")
        .required("送付するポイントを入力してください。")
        .min(min, `送付するポイントは ${this.addComma(min)} 以上でなければなりません。`)
        .max(fallbackMax, `送付するポイントは ${this.addComma(fallbackMax)} 以下でなければなりません。`),

        availablePointRange: (() => {
          const range = this.availablePointRange;
          if (range) {
            return yup.number()
              .typeError("送付するポイントは数字で入力してください。")
              .integer("送付するポイントは整数で入力してください。")
              .required("送付するポイントを入力してください。")
              .min(range.min, `送付するポイントは ${this.addComma(range.min)} 以上でなければなりません。`)
              .max(range.max, `送付するポイントは ${this.addComma(range.max)} 以下でなければなりません。`);
          } else {
            return yup.number()
              .typeError("送付するポイントは数字で入力してください。")
              .required("送付するポイントを入力してください。")
              .min(min, `送付するポイントは ${this.addComma(min)} 以上でなければなりません。`)
              .max(fallbackMax, `送付するポイントは ${this.addComma(fallbackMax)} 以下でなければなりません。`);
          }
        })(),

      object: yup.string()
        .required("送付するポイントを選択してください。")
        .test(
          "point-max",
          `送付するポイントは ${this.addComma(fallbackMax)} 以下を選択してください。`,
           value => {
             if (!value) {
               return true;
             }
             return Number(value) <= fallbackMax;
           }
        ),

      number: yup.number()
        .typeError("送付するポイントは数字で入力してください。")
        .max(fallbackMax, `送付するポイントは ${this.addComma(fallbackMax)} 以下でなければなりません。`)
    };

	if (this.pointTypeOf === "number") {
	  this.formSchema = yup.object({
	    title: yup.string()
	      .required("タイトルを入力してください。")
	      .max(100, "タイトルは100文字以内で入力してください。")
	      .transform(value => value?.trim() ?? "")
	      .min(1,""),
	    message: yup.string()
	      .required("本文を入力してください。")
	      .max(500, "本文は500文字以内で入力してください。")
	      .transform(value => value?.trim() ?? "")
	      .min(1,"")
	  });
	} else {
	  this.formSchema = yup.object({
	    sendPoint: pointRuleMap[this.pointTypeOf],
	    title: yup.string()
	      .required("タイトルを入力してください。")
	      .max(100, "タイトルは100文字以内で入力してください。")
	      .transform(value => value?.trim() ?? "")
	      .min(1,""),
	    message: yup.string()
	      .required("本文を入力してください。")
	      .max(500, "本文は500文字以内で入力してください。")
	      .transform(value => value?.trim() ?? "")
	      .min(1,"")
	  });
	}
  }

  submit(values: any) {
    const pointsToSend = values.sendPoint;

    if (this.usedPoints > this.availablePoints) {
      alert("送付ポイントが贈与可能ポイントを超えています。");
      return;
    }

    this.$emit(
      "show-confirm",
      {
        sendPoint: pointsToSend,
        title: values.title,
        message: values.message
      }
    );
  }

  notifySendPoint(value: any) {
    this.$emit("send-point-change", Number(value) || 0);
  }

  get showUsedPoints() {
    return this.selectedUserCount > 1;
  }

  get canSubmit() {
    const badgeValid = !this.giftBadgeRequired || this.selectedGiftBadgeId !== null;

    return (
      this.selectedUserCount > 0 &&
      this.usedPoints <= this.availablePoints &&
      badgeValid
    );
  }

  get pointOver() {
    return this.selectedUserCount > 1 &&
    this.usedPoints > this.availablePoints;
  }

  setBadgeContent(title?: string | null, message?: string | null, clear = false) {
    const form = this.$refs.nativeForm as any;

    if (!form) {
      return;
    }

    const shouldOverwriteTitle = !this.titleEdited || !this.titleEditEnabled;
    const shouldOverwriteMessage = !this.messageEdited;

    if (clear) {
      if (shouldOverwriteTitle) {
        form.setFieldValue("title", "");
      }

      if (shouldOverwriteMessage) {
        form.setFieldValue("message", "");
      }

      if (!this.titleEditEnabled) {
        this.titleEdited = false;
      }

      return;
    }

    if (shouldOverwriteTitle) {
      form.setFieldValue("title", title ?? "");
    }

    if (shouldOverwriteMessage) {
      form.setFieldValue("message", message ?? "");
    }

    if (!this.titleEditEnabled) {
      this.titleEdited = false;
    }
  }

  getCurrentSendPoint() {
    const form = this.$refs.nativeForm as any;
    const value = form?.values?.sendPoint;

    if (
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      return value;
    }

    if (this.pointTypeOf === "number") {
      return this.fixedPoint;
    }

    return null;
  }

  setSendPoint(value: any) {
    const form = this.$refs.nativeForm as any;
    form.setFieldValue("sendPoint", value);
    this.notifySendPoint(value);
  }

  get displayFixedPoint() {
    const form = this.$refs.nativeForm as any;
    const value = form?.values?.sendPoint;

    if (
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return this.fixedPoint;
    }
    return value;
  }

  onTitleChange(value: any) {
    this.titleEdited = String(value ?? "").trim().length > 0;
  }

  onMessageChange(value: any) {
    this.messageEdited = String(value ?? "").trim().length > 0;
  }
}
</script>

<style lang="scss" scoped>
.p-the-j-input-screen {
  &__label {
    @include sp {
      font-size: rem(14);
    }
    line-height: 1.5;
    font-weight: bold;
    margin-right: 0.5em;
    display: block;
  }

  &__point {
    &__col {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__input {
      width: rem(145);
      min-width: rem(145);
      margin-right: rem(8);
    }

    &__select {
      width: rem(120);
      min-width: rem(120);
      margin-right: rem(8);
    }

    &__text {
      font-size: $text-size__body--xl;
      margin-right: rem(2);
    }

    &__note {
      display: block;
      width: 100%;
      margin-top: rem(8);
    }

    &__unit {
      font-size: rem(12);
      display: block;
    }
  }
}
.p-point-summary {
  display: flex;

  align-items: flex-start;

  gap: rem(48);

  margin-bottom: rem(32);

  @include sp {
    flex-direction: column;

    gap: rem(16);
  }
}
.p-cmt {
    color: #828282;
    font-size: .75rem;
    margin-top: 0.375rem !important;
    margin-bottom: 0;
}
</style>
